import React from "react";
import { connect } from "react-redux";
import { bindActionCreators } from "redux";
import { injectIntl } from "react-intl";
import clsx from "clsx";

import { withTheme, withStyles } from "@material-ui/core/styles";
import { Divider, Grid, Paper, Typography, FormControlLabel, Checkbox, IconButton } from "@material-ui/core";
import { Add as AddIcon, Autorenew as RenewIcon, Delete as DeleteIcon, Pause as SuspendIcon } from "@material-ui/icons";

import {
  Table,
  PagedDataHandler,
  formatMessage,
  formatMessageWithValues,
  formatDateFromISO,
  withModulesManager,
  formatSorter,
  sort,
  withTooltip,
  historyPush,
  withHistory,
  coreConfirm,
  journalize,
  AmountInput,
} from "@openimis/fe-core";
import { fetchFamilyOrInsureePolicies, selectPolicy, deletePolicy, suspendPolicy } from "../actions";
import { RIGHT_POLICY_ADD, FAMILY_TYPE_POLYGAMY_CODE } from "../constants";
import { policyLabel, canDeletePolicy, canSuspendPolicy, canRenewPolicy } from "../utils/utils";

const styles = (theme) => ({
  paper: {
    ...theme.paper.paper,
  },
  paperHeader: {
    ...theme.paper.header,
  },
  tableTitle: theme.table.title,
  title: {
    ...theme.table.title,
    padding: 0,
  },
  fab: theme.fab,
  button: {
    margin: theme.spacing(1),
  },
  item: {
    padding: theme.spacing(1),
  },
});

class FamilyOrInsureePoliciesSummary extends PagedDataHandler {
  state = {
    page: 0,
    pageSize: this.props.modulesManager.getConf(
      "fe-policy",
      "familyOrInsureePoliciesSummary.defaultPageSize",
      5
    ),
    afterCursor: null,
    beforeCursor: null,
  };

  constructor(props) {
    super(props);
    this.rowsPerPageOptions = props.modulesManager.getConf(
      "fe-policy",
      "familyOrInsureePoliciesSummary.rowsPerPageOptions",
      [5, 10, 20]
    );
    this.defaultPageSize = props.modulesManager.getConf(
      "fe-policy",
      "familyOrInsureePoliciesSummary.defaultPageSize",
      5
    );
    this.showBalance = props.modulesManager.getConf(
      "fe-policy",
      "familyOrInsureePoliciesSummary.showBalance",
      false
    );
    this.onlyActiveOrLastExpired = props.modulesManager.getConf(
      "fe-policy",
      "familyOrInsureePoliciesSummary.onlyActiveOrLastExpired",
      true
    );
    this.orderByExpiryDate = props.modulesManager.getConf(
      "fe-policy",
      "familyOrInsureePoliciesSummary.orderByExpiryDate",
      "expiryDate"
    );
    this.columns = this.props.modulesManager.getConf("fe-policy", "columns", {
      deduction: "H",
      hospitalDeduction: "H",
      nonHospitalDeduction: "H",
      ceiling: "H",
      hospitalCeiling: "H",
      nonHospitalCeiling: "H",
    });
  }

  isColumnVisible = (key) => !!this.columns?.[key] && this.columns[key] !== "H";

  configurableColumns = [
    { key: "deduction", header: "policies.deduction", sorter: "deduction", format: (i) => i.ded },
    { key: "hospitalDeduction", header: "policies.hospitalDeduction", sorter: "hospitalDeduction", format: (i) => i.dedInPatient },
    { key: "nonHospitalDeduction", header: "policies.nonHospitalDeduction", sorter: "nonHospitalDeduction", format: (i) => i.dedOutPatient },
    { key: "ceiling", header: "policies.ceiling", sorter: "ceiling", format: (i) => i.ceiling },
    { key: "hospitalCeiling", header: "policies.hospitalCeiling", sorter: "hospitalCeiling", format: (i) => i.ceilingInPatient },
    { key: "nonHospitalCeiling", header: "policies.nonHospitalCeiling", sorter: "nonHospitalCeiling", format: (i) => i.ceilingOutPatient },
  ];

  visibleConfigurableColumns = () => this.configurableColumns.filter((c) => this.isColumnVisible(c.key));

  componentDidMount() {
    this.setState(
      {
        confirmedAction: null,
        onlyActiveOrLastExpired: this.onlyActiveOrLastExpired,
        orderBy: this.orderByExpiryDate,
        showDeletedPolicies: false,
      },
      (e) => this.query()
    );
  }

  componentDidUpdate(prevProps, prevState, snapshot) {
    if (this.insureeChanged(prevProps) || this.familyChanged(prevProps)) {
      this.query();
    } else if (!prevProps.confirmed && this.props.confirmed && !!this.state.confirmedAction) {
      this.state.confirmedAction();
    } else if (prevProps.submittingMutation && !this.props.submittingMutation) {
      this.props.journalize(this.props.mutation);
      this.setState({ reset: this.state.reset + 1 });
    }
  }

  addNewPolicy = () =>
    historyPush(this.props.modulesManager, this.props.history, "policy.route.policy", [
      "_NEW_",
      this.props.family.uuid,
    ]);

  renewPolicy = (i) =>
    historyPush(this.props.modulesManager, this.props.history, "policy.route.policy", [
      i.policyUuid,
      this.props.family.uuid,
      true,
    ]);

  confirmSuspend = (policy) => {
    policy.family = this.props.family;
    let confirmedAction = () =>
      this.props.suspendPolicy(
        this.props.modulesManager,
        policy,
        formatMessageWithValues(this.props.intl, "policy", "SuspendPolicy.mutationLabel", {
          policy: policyLabel(this.props.modulesManager, policy),
        })
      );
    let confirm = (e) =>
      this.props.coreConfirm(
        formatMessageWithValues(this.props.intl, "policy", "suspendPolicyDialog.title", {
          label: policyLabel(this.props.modulesManager, policy),
        }),
        formatMessageWithValues(this.props.intl, "policy", "suspendPolicyDialog.message", {
          label: policyLabel(this.props.modulesManager, policy),
        })
      );
    this.setState({ confirmedAction }, confirm);
  };

  confirmDelete = (policy) => {
    let confirmedAction = () =>
      this.props.deletePolicy(
        this.props.modulesManager,
        policy,
        formatMessageWithValues(this.props.intl, "policy", "DeletePolicy.mutationLabel", {
          policy: policyLabel(this.props.modulesManager, policy),
        })
      );
    let confirm = (e) =>
      this.props.coreConfirm(
        formatMessageWithValues(this.props.intl, "policy", "deletePolicyDialog.title", {
          label: policyLabel(this.props.modulesManager, policy),
        }),
        formatMessageWithValues(this.props.intl, "policy", "deletePolicyDialog.message", {
          label: policyLabel(this.props.modulesManager, policy),
        })
      );
    this.setState({ confirmedAction }, confirm);
  };

  onDoubleClick = (i, newTab = false) => {
    historyPush(this.props.modulesManager, this.props.history, "policy.route.policy", [
      i.policyUuid,
      this.props.family.uuid,
    ]);
  };

  insureeChanged = (prevProps) =>
    (!prevProps.insuree && !!this.props.insuree) ||
    (!!prevProps.insuree && !this.props.insuree) ||
    (!!prevProps.insuree &&
      !!this.props.insuree &&
      !!this.props.insuree.chfId &&
      (prevProps.insuree.chfId == null || prevProps.insuree.chfId !== this.props.insuree.chfId));
  familyChanged = (prevProps) =>
    (!prevProps.family && !!this.props.family) ||
    (!!prevProps.family && !this.props.family) ||
    (!!prevProps.family &&
      !!this.props.family &&
      !!this.props.family.uuid &&
      (prevProps.family.uuid == null || prevProps.family.uuid !== this.props.family.uuid));

  queryPrms() {
    let prms = [
      `orderBy: "${this.state.orderBy}"`, 
      `activeOrLastExpiredOnly: ${this.state.showDeletedPolicies ? false : !!this.state.onlyActiveOrLastExpired}`
    ];
    if (this.state.showDeletedPolicies) {
      prms.push("showDeleted: true");
    }
    if (!!this.props.insuree && !!this.props.insuree.chfId) {
      prms.push(`chfId:"${this.props.insuree.chfId}"`);
      return prms;
    } else if (!!this.props.family && !!this.props.family.uuid) {
      prms.push(`familyUuid:"${this.props.family.uuid}"`);
      return prms;
    }
  }

  onChangeSelection = (i) => {
    const { selectPolicy, disableSelection } = this.props;

    if (disableSelection) {
      return;
    }

    selectPolicy(i[0] || null);
  };

  toggleCheckbox = (key) => {
    this.setState(
      (state, props) => ({
        [key]: !state[key],
      }),
      (e) => this.query()
    );
  };

  headers = () => {
    let h = [
      "policies.contributionPlanCode",
      "policies.contributionPlanName",
      "policies.effectiveDate",
      "policies.enrolmentDate",
      "policies.expiryDate",
      "policies.status",
      "policies.policyValue",
    ];
    this.visibleConfigurableColumns().forEach((c) => h.push(c.header));
    if (this.showBalance) {
      h.push("policies.balance");
    }
    h.push("", "", "");
    return h;
  };

  sorter = (attr, asc = true) => [
    () =>
      this.setState(
        (state, props) => ({ orderBy: sort(state.orderBy, attr, asc) }),
        (e) => this.query()
      ),
    () => formatSorter(this.state.orderBy, attr, asc),
  ];

  headerActions = () => {
    let a = [
      this.sorter("contributionPlanCode"),
      this.sorter("contributionPlanName"),
      this.sorter("effectiveDate"),
      this.sorter("enrolmentDate"),
      this.sorter("expiryDate"),
      this.sorter("status"),
      this.sorter("policyValue"),
    ];
    this.visibleConfigurableColumns().forEach((c) => a.push(this.sorter(c.sorter)));
    if (this.showBalance) {
      a.push(this.sorter("balance"));
    }
    return a;
  };

  rowLocked = (policy) => !!policy.clientMutationId;
  canDelete = (policy) => !this.props.readOnly && canDeletePolicy(this.props.rights, policy);
  canSuspend = (policy) => !this.props.readOnly && canSuspendPolicy(this.props.rights, policy);
  canRenew = (policy) => !this.props.readOnly && canRenewPolicy(this.props.rights, policy);
  rowSecondaryHighlighted = (policy) => !!policy?.validityTo;

  itemFormatters = () => {
    let f = [
      (i) => i.contributionPlanCode,
      (i) => i.contributionPlanName,
      (i) => formatDateFromISO(this.props.modulesManager, this.props.intl, i.effectiveDate),
      (i) => formatDateFromISO(this.props.modulesManager, this.props.intl, i.enrollDate),
      (i) => formatDateFromISO(this.props.modulesManager, this.props.intl, i.expiryDate),
      (i) => formatMessage(this.props.intl, "policy", `policies.status.${i.status}`),
      (i) => <AmountInput value={i.policyValue} readOnly />,
    ];
    this.visibleConfigurableColumns().forEach((c) => f.push(c.format));
    if (this.showBalance) {
      f.push((i) => i.balance);
    }
    // f.push((i) =>
    //   !this.props.readOnly && this.canRenew(i)
    //     ? withTooltip(
    //         <IconButton onClick={(e) => this.renewPolicy(i)}>
    //           <RenewIcon />
    //         </IconButton>,
    //         formatMessage(this.props.intl, "policy", "action.RenewPolicy.tooltip")
    //       )
    //     : null
    // );
    f.push((i) =>
      !this.props.readOnly && this.canSuspend(i)
        ? withTooltip(
            <IconButton onClick={(e) => this.confirmSuspend(i)}>
              <SuspendIcon />
            </IconButton>,
            formatMessage(this.props.intl, "policy", "action.SuspendPolicy.tooltip")
          )
        : null
    );
    f.push((i) =>
      !this.props.readOnly && this.canDelete(i)
        ? withTooltip(
            <IconButton onClick={(e) => this.confirmDelete(i)}>
              <DeleteIcon />
            </IconButton>,
            formatMessage(this.props.intl, "policy", "action.DeletePolicy.tooltip")
          )
        : null
    );
    return f;
  };

  header = () => {
    const { intl, pageInfo, insuree } = this.props;
    if (insuree?.chfId) {
      return formatMessageWithValues(intl, "policy", "policiesOfInsuree.header", {
        count: pageInfo.totalCount,
        chfId: insuree.chfId,
      });
    } else {
      return formatMessageWithValues(intl, "policy", "policies.header", { count: pageInfo.totalCount });
    }
  };

  itemIdentifier = (i) => i.policyUuid;

  render() {
    const {
      intl,
      classes,
      rights,
      fetchingPolicies,
      policies,
      pageInfo,
      errorPolicies,
      family,
      insuree,
      readOnly,
      edited,
      className,
      hideAddPolicyButton = false,
      disableSelection,
    } = this.props;
    if (((!family || !family.uuid) && (!insuree || !insuree.uuid) )|| (!!family.familyType && family.familyType.code == FAMILY_TYPE_POLYGAMY_CODE) || (!!edited && !!edited.familyType && edited.familyType.code == FAMILY_TYPE_POLYGAMY_CODE )) {
      return null;
    }

    let actions =
      hideAddPolicyButton || !!readOnly || !rights.includes(RIGHT_POLICY_ADD) 
        ? []
        : [
            {
              button: (
                <IconButton onClick={this.addNewPolicy}>
                  <AddIcon />
                </IconButton>
              ),
              tooltip: formatMessage(intl, "policy", "action.AddPolicy.tooltip"),
            },
          ];

    return (
      <Paper className={clsx(classes.paper, className)}>
        <Grid
          container
          justifyContent="space-between"
          alignItems="center"
          className={clsx(classes.paperHeader, classes.tableTitle)}
        >
          <Grid item>
            <Typography className={classes.title}>{this.header()}</Typography>
          </Grid>
          <Grid item>
            <Grid container alignItems="center" spacing={3}>
              <Grid item>
                <FormControlLabel
                  control={
                    <Checkbox
                      color="primary"
                      checked={!!this.state.onlyActiveOrLastExpired}
                      onChange={(e) => this.toggleCheckbox("onlyActiveOrLastExpired")}
                    />
                  }
                  label={formatMessage(intl, "policy", "policies.onlyActiveOrLastExpired")}
                />
              </Grid>
              <Grid item>
                <FormControlLabel
                  control={
                    <Checkbox
                      color="primary"
                      checked={!!this.state.showDeletedPolicies}
                      onChange={(e) => this.toggleCheckbox("showDeletedPolicies")}
                    />
                  }
                  label={formatMessage(intl, null, "showDeleted")}
                />
              </Grid>
              {actions.map((a, idx) => {
                return (
                  <Grid item key={`form-action-${idx}`}>
                    {withTooltip(a.button, a.tooltip)}
                  </Grid>
                );
              })}
            </Grid>
          </Grid>
        </Grid>
        <Divider />
        <Table
          module="policy"
          headers={this.headers()}
          headerActions={this.headerActions()}
          itemFormatters={this.itemFormatters()}
          itemIdentifier={this.itemIdentifier}
          items={policies}
          fetching={fetchingPolicies}
          error={errorPolicies}
          withSelection={disableSelection ? false : "single"}
          onChangeSelection={this.onChangeSelection}
          onDoubleClick={this.onDoubleClick}
          withPagination={true}
          rowsPerPageOptions={this.rowsPerPageOptions}
          defaultPageSize={this.defaultPageSize}
          page={this.state.page}
          pageSize={this.state.pageSize}
          count={pageInfo.totalCount}
          onChangePage={this.onChangePage}
          onChangeRowsPerPage={this.onChangeRowsPerPage}
          rowLocked={this.rowLocked}
          rowSecondaryHighlighted={this.rowSecondaryHighlighted}
        />
      </Paper>
    );
  }
}

const mapStateToProps = (state) => ({
  rights: !!state.core && !!state.core.user && !!state.core.user.i_user ? state.core.user.i_user.rights : [],
  fetchingPolicies: state.policy.fetchingPolicies,
  fetchedPolicies: state.policy.fetchedPolicies,
  policies: state.policy.policies,
  pageInfo: state.policy.policiesPageInfo,
  errorPolicies: state.policy.errorPolicies,
  family: state.insuree.family || {},
  insuree: state.insuree.insuree,
  confirmed: state.core.confirmed,
  submittingMutation: state.policy.submittingMutation,
  mutation: state.policy.mutation,
});

const mapDispatchToProps = (dispatch) => {
  return bindActionCreators(
    { fetch: fetchFamilyOrInsureePolicies, selectPolicy, deletePolicy, suspendPolicy, coreConfirm, journalize },
    dispatch
  );
};

export default withHistory(
  withModulesManager(
    connect(
      mapStateToProps,
      mapDispatchToProps
    )(injectIntl(withTheme(withStyles(styles)(FamilyOrInsureePoliciesSummary))))
  )
);
